import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {getSpecimenGuideContent} from '@/content/specimenGuide'
import {Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {SpecimenGuideSpecimen} from '@/utils/specimen-guide/types'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {ContentLayoutWithSidebar} from '../common/content/ContentLayoutWithSidebar'
import {ContentWithInnerHTML} from '../common/content/ContentWithInnerHTML'
import {CheckCircleIcon} from '../common/icons/CheckCircleIcon'
import {CloseCircleIcon} from '../common/icons/CloseCircleIcon'
import {SpecimenGuideSidebarCard} from './SpecimenGuideSidebarCard'

type SpecimenGuideContentProps = {
  name: string
  text: string
  specimens: SpecimenGuideSpecimen[]
}

export const SpecimenGuideContent: FC<SpecimenGuideContentProps> = ({name, text, specimens}) => {
  const {t} = useTranslation()
  const {examplesTitle, sourceTitle, imageAtFullSizeTitle} = getSpecimenGuideContent().guide

  return (
    <>
      <ContentLayoutWithSidebar sidebarCard={<SpecimenGuideSidebarCard />}>
        <ContentWithInnerHTML content={text} />
        <div className="title">
          <Text variant={TextVariant.h3} element="h2">
            {t(examplesTitle, {name})}
          </Text>
        </div>
        {specimens.map(({id, text, isSuitable, image, imageLink, filingLink}) => (
          <div key={id} className="specimen">
            {/* Plain <img>: these are S3 photos of unknown size, which next/image needs upfront.
                They all sit below the guide's intro text, so they can load lazily. */}
            <img src={image} alt={text} loading="lazy" decoding="async" className="image" />
            <div className="description">
              <div className="icon">
                {isSuitable ? (
                  <CheckCircleIcon
                    checkColor={theme.palette.success.main}
                    backgroundColor={theme.palette.success.light}
                  />
                ) : (
                  <CloseCircleIcon />
                )}
              </div>
              <Text variant={TextVariant.h5} element="p" fontWeight={TextFontWeight.regular}>
                {text}{' '}
                {filingLink && (
                  <a href={filingLink} target="_blank" rel="noreferrer" className="underline">
                    {t(sourceTitle)}
                  </a>
                )}
                {imageLink && (
                  <Trans i18nKey={imageAtFullSizeTitle}>
                    text
                    <a href={imageLink} target="_blank" rel="noreferrer" className="underline">
                      text
                    </a>
                  </Trans>
                )}
              </Text>
            </div>
          </div>
        ))}
      </ContentLayoutWithSidebar>

      <style jsx>{`
        .title {
          margin-top: ${theme.spacing[7]};
          margin-bottom: ${theme.spacing[5]};
        }
        .specimen + .specimen {
          margin-top: ${theme.spacing[8]};
        }
        .image {
          width: auto;
          max-width: 100%;
          height: auto;
          display: block;
        }
        .description {
          display: flex;
          margin-top: ${theme.spacing[4]};
        }
        .icon {
          margin-top: ${theme.spacing[1.5]};
          margin-right: ${theme.spacing[2.5]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .specimen + .specimen {
            margin-top: ${theme.spacing[7]};
          }
          .icon {
            margin-top: ${theme.spacing[0.5]};
          }
        }
      `}</style>
    </>
  )
}
